/**
 * Zustand store — настройки приложения.
 * Persist middleware сохраняет в localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { BotConfig } from '@tg-miniapp/shared-types';

/** Роль текущего пользователя в Mini App */
type UserRole = 'owner' | 'manager' | 'assistant' | 'master' | 'admin';

interface AppSettingsState {
  theme: 'auto' | 'light' | 'dark';
  language: 'ru' | 'en';
  notificationsEnabled: boolean;
  lastSyncAt: number;
  currentRole: UserRole;
  currentBusinessId: string | null;
  onboardingComplete: boolean;
  config: BotConfig | null;
}

interface AppSettingsActions {
  setTheme: (theme: AppSettingsState['theme']) => void;
  setLanguage: (language: AppSettingsState['language']) => void;
  setCurrentRole: (role: UserRole) => void;
  setCurrentBusinessId: (id: string | null) => void;
  setOnboardingComplete: (complete: boolean) => void;
  setLastSyncAt: (timestamp: number) => void;
  setConfig: (config: BotConfig | null) => void;
}

export const useAppSettings = create<AppSettingsState & AppSettingsActions>()(
  persist(
    (set) => ({
      // State
      theme: 'auto',
      language: 'ru',
      notificationsEnabled: true,
      lastSyncAt: 0,
      currentRole: 'owner',
      currentBusinessId: null,
      onboardingComplete: false,
      config: null,

      // Actions — pure setters, no side effects
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
      setCurrentRole: (currentRole) => set({ currentRole }),
      setCurrentBusinessId: (currentBusinessId) => set({ currentBusinessId }),
      setOnboardingComplete: (onboardingComplete) => set({ onboardingComplete }),
      setLastSyncAt: (lastSyncAt) => set({ lastSyncAt }),
      setConfig: (config) => set({ config }),
    }),
    { name: 'app-settings' },
  ),
);
